<i18n lang="json">
{
  "en": {
    "title": "List your inventory",
    "intro": "Welcome to RVezy's marketplace, if you're a dealer looking to showcase your RV inventory  you've arrived at the right place. Fill out the form below and we will contact you soon so you can provide us with your inventory feed details, and let us take care of the rest, ensuring your RVs get the visibility they deserve.",
    "getStarted": "Get started",
    "dealershipInformation": "Dealership information",
    "submit": "Submit",
    "form": {
      "contactName": "Contact Name",
      "contactPhoneNumber": "Phone number",
      "contactEmailAddress": "Email Address",
      "dealerName": "Dealership Name",
      "website": "Website",
      "numberOfLocations": "Number of Locations"
    },
    "thanksForSubmitting": ["Thanks for your inquiry!", "We'll be in touch with you about the next steps."]
  },
  "fr": {
    "title": "Listez votre inventaire",
    "intro": "Bienvenue sur le marché de RVezy, si vous êtes un concessionnaire cherchant à présenter votre inventaire de VR, vous êtes au bon endroit. Remplissez le formulaire ci-dessous et nous vous contacterons bientôt pour que vous puissiez nous fournir les détails de votre flux d'inventaire, et nous nous occuperons du reste, en assurant à vos VR la visibilité qu'ils méritent.",
    "getStarted": "Commencer",
    "dealershipInformation": "Informations sur la concession",
    "submit": "Soumettre",
    "form": {
      "contactName": "Nom du contact",
      "contactPhoneNumber": "Numéro de téléphone",
      "contactEmailAddress": "Courriel",
      "dealerName": "Nom de la concession",
      "website": "Site web",
      "numberOfLocations": "Nombre de sites"
    },
    "thanksForSubmitting": ["Merci pour votre demande !", "Nous vous contacterons pour les prochaines étapes."]
  }
}
</i18n>

<template>
  <Container class="mb-4 pt-6">
    <h1 class="typography-title-1 mb-6">{{ t('title') }}</h1>

    <div v-if="error" class="typography-strong-1 text-danger">
      {{ error }}
    </div>

    <template v-if="formStep === 1">
      <div class="mb-4">{{ t('intro') }}</div>

      <div class="typography-subtitle-0 mb-4">{{ t('dealershipInformation') }}</div>

      <div class="max-w-96">
        <label>
          <div class="typography-strong-1">{{ t('form.dealerName') }}</div>
          <FormText
            v-model.trim="state.dealerName"
            :error="v$.dealerName.$error"
            :valid="v$.dealerName.$dirty && !v$.dealerName.$invalid"
            @blur="v$.dealerName.$validate"
          />
        </label>

        <label>
          <div class="typography-strong-1">{{ t('form.contactName') }}</div>
          <FormText
            autocomplete="name"
            v-model.trim="state.contactName"
            :error="v$.contactName.$error"
            :valid="v$.contactName.$dirty && !v$.contactName.$invalid"
            @blur="v$.contactName.$validate"
          />
        </label>

        <label>
          <div class="typography-strong-1">{{ t('form.contactEmailAddress') }}</div>
          <FormText
            type="email"
            autocomplete="email"
            v-model.trim="state.contactEmailAddress"
            :error="v$.contactEmailAddress.$error"
            :valid="v$.contactEmailAddress.$dirty && !v$.contactEmailAddress.$invalid"
            @blur="v$.contactEmailAddress.$validate"
          />
        </label>

        <label>
          <div class="typography-strong-1">{{ t('form.contactPhoneNumber') }}</div>
          <FormText
            v-model.trim="state.contactPhoneNumber"
            :error="v$.contactPhoneNumber.$error"
            :valid="v$.contactPhoneNumber.$dirty && !v$.contactPhoneNumber.$invalid"
            @blur="v$.contactPhoneNumber.$validate"
          />
        </label>

        <label>
          <div class="typography-strong-1">{{ t('form.website') }}</div>
          <FormText
            type="url"
            v-model.trim="state.website"
            :error="v$.website.$error"
            :valid="v$.website.$dirty && !v$.website.$invalid"
            @blur="v$.website.$validate"
          />
        </label>

        <label>
          <div class="typography-strong-1">{{ t('form.numberOfLocations') }}</div>
          <FormText
            type="number"
            min="1"
            v-model="state.numberOfLocations"
            :error="v$.numberOfLocations.$error"
            :valid="v$.numberOfLocations.$dirty && !v$.numberOfLocations.$invalid"
            @blur="v$.numberOfLocations.$validate"
          />
        </label>

        <div class="grid grid-cols-2 gap-4">
          <FormButton class="col-start-2" variant="highlight" @click="submitHandler">{{ t('submit') }}</FormButton>
        </div>
      </div>
    </template>

    <template v-if="formStep === 2">
      <div v-for="(text, key) in tm('thanksForSubmitting')" :key="key" class="typography-body-1">
        {{ rt(text as any) }}
      </div>
    </template>
  </Container>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { SegmentEvents } from '~/enums/segment-events'
import { segmentTrack } from '~/lib/tracking'

const { t, tm, rt } = useI18n()
const { getMarketplaceApiClient } = useApiClients()

const formStep = ref(1)
const error = ref<string | null>()

const state = reactive({
  contactName: undefined,
  contactEmailAddress: undefined,
  contactPhoneNumber: undefined,
  dealerName: undefined,
  numberOfLocations: 0,
  website: undefined
})

const rules = {
  contactName: { required },
  contactEmailAddress: { required },
  contactPhoneNumber: { required },
  dealerName: { required },
  numberOfLocations: { required },
  website: { required }
}

const v$ = useVuelidate(rules, state as any)

const submitHandler = async () => {
  error.value = null

  v$.value.$reset()
  const isFormValid = await v$.value.$validate()
  if (!isFormValid) return

  try {
    await getMarketplaceApiClient().POST('/LeadDealer', {
      body: {
        numberOfLocations: state.numberOfLocations,
        contactEmailAddress: state.contactEmailAddress,
        contactName: state.contactName,
        contactPhoneNumber: state.contactPhoneNumber,
        dealerName: state.dealerName,
        website: state.website
      }
    })
  } catch (e: any) {
    error.value = e?.message || 'An error has occurred, please try again'
    formStep.value = 1
    return
  }

  segmentTrack(SegmentEvents.CTAClicked, {
    name: 'DealerSellerSubmitted'
  })

  formStep.value++
  v$.value.$reset()
}
</script>
